<template>
  <div>
    <v-container class="container px-0">
      <v-row
        class="title-row"
        :class="`${
          $vuetify.breakpoint.mdAndDown ? 'flex-column flex-nowrap' : ''
        }`"
      >
        <v-col
          class="d-flex align-center"
          :style="
            $vuetify.breakpoint.width <= 1400
              ? ''
              : 'min-width: calc(100% - 482px); max-width: calc(100% - 482px)'
          "
        >
          <h4 class="font-weight-light">{{ $t('heading.hosting.emailAddress.title') }}</h4>
          <v-select
            :label="$t('heading.hosting.emailDomain.title')"
            class="ml-2 dns-zone"
            outlined
            style="max-width: calc(100% - 102px)"
            :hide-details="true"
            v-model="selectedEmailDomain"
            :items="emailDomains.items"
            item-text="domain"
            item-value="domain"
            :loading="emailDomains.loading"
            @change="reloadEmailAccounts"
            return-object
            :menu-props="{
              offsetY: true,
              nudgeBottom: '8px',
            }"
          />
        </v-col>

        <v-col
          class="page-title__filter-controls justify-end"
          :class="[`${$vuetify.breakpoint.mobile ? 'pb-3' : ''}`]"
        >
          <template v-if="!emailAccounts.loading">
            <data-iterator-filter-controls
              v-if="emailAccounts.items.length"
              :keys="keys"
              :showSearchTerm="true"
              :searchTerm="searchTerm"
              :fullWidthSearch="false"
              :fullWidthLg="false"
              :fullWidthMd="true"
              @update:sortBy="changeSortBy"
              @update:sortDesc="handleSortDescChange"
              @update:searchTerm="handleSearchTermChange"
            ></data-iterator-filter-controls>

            <v-btn
              v-if="emailAccounts.items.length"
              :large="!$vuetify.breakpoint.smAndDown"
              elevation="0"
              color="primary"
              class="p-2 add-new-button page-title__add-button"
              @click="showAddModal()"
              :loading="addButtonLoading"
            >
              <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
              >$plus
              </v-icon
              >
              {{ $vuetify.breakpoint.mobile ? "" : "Add Address" }}
            </v-btn>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <v-container class="mt-3 px-0">
      <v-row>
        <v-col cols="12">
          <div :class="checkedItems.length > 0 ? 'pb-15' : ''">
            <hosting-table
              v-if="!isError"
              :checkedItems="checkedItems"
              :headers="headers"
              :items="emailAccounts.items"
              :itemsLoading="emailAccounts.loading"
              :itemsPerPage="itemsPerPage"
              :itemsTotal="itemsTotal"
              :page="page"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :searchTerm="searchTerm"
              :sortBy="sortBy"
              :sortDesc="sortDesc"
              @clearFilters="searchTerm = ''"
              @update:check-all="handleSelectAll"
              @update:checked-items="handleItemCheck"
              @update:itemsPerPage="changePerPage"
              @update:page="changePage"
              @update:sortBy="changeSortBy"
              @update:sortDesc="changeSortDesc"
              :itemClass="itemClass"
            >
              <template v-slot:no-data>
                <TableFullScreenMessage
                  :title="$t('message.emptyTable.emailAddress.title')"
                  :desc="$t('message.emptyTable.emailAddress.description')"
                >
                  <template v-slot:image>
                    <hosting-email-addresses-illustration/>
                  </template>
                  <template v-slot:action>
                    <v-btn
                      :large="!$vuetify.breakpoint.smAndDown"
                      :small="$vuetify.breakpoint.smAndDown"
                      elevation="0"
                      color="primary"
                      class="p-2 add-new-button"
                      @click="showAddModal()"
                      :loading="addButtonLoading"
                    >
                      <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
                      >$plus
                      </v-icon
                      >
                      {{
                        $vuetify.breakpoint.mobile ? "" : $t('button.emailAddress.add')
                      }}
                    </v-btn>
                  </template>
                </TableFullScreenMessage>
              </template>
              <template v-slot:actions="item">
                <v-tooltip
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  z-index="99"
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="showEditModal(item)"
                    >
                      <v-icon>$edit2</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.emailAddress.update') }}</span>
                </v-tooltip>
                <v-tooltip
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  z-index="99"
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="loadDetailsModal(item)"
                      :loading="item.detailsButtonLoading"
                    >
                      <v-icon>$eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.emailAddress.show') }}</span>
                </v-tooltip>
                <v-tooltip
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  z-index="99"
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="showDeleteModal(item)"
                    >
                      <v-icon>$thrash</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.emailAddress.delete') }}</span>
                </v-tooltip>
                <v-tooltip
                  transition="custom-tooltip"
                  open-delay="150"
                  bottom
                  z-index="99"
                  offset-overflow
                  nudge-bottom="4px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      icon
                      small
                      @click="webmailSso(item)"
                      :loading="item.webmailButtonLoading"
                    >
                      <v-icon>$mail</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('button.emailAddress.logIn') }}</span>
                </v-tooltip>
              </template>

              <template v-slot:mobile="{ item, headers }">
                <div class="mobile-table-item__row">
                  <div
                    class="mobile-table-item__header"
                    @click="$emit('update:sortBy', headers[0])"
                  >
                    {{ $t('table.header.email') }}
                  </div>
                  <div class="mobile-table-item__value">
                    <span class="bold p-3">{{ item.email }}</span>
                  </div>
                </div>
                <div class="mobile-table-item__row">
                  <div
                    class="mobile-table-item__header"
                    @click="$emit('update:sortBy', headers[0])"
                  >
                    {{ $t('table.header.usageQuota') }}
                  </div>
                  <div class="mobile-table-item__value">
                    <span class="bold p-3">{{ item.usage_quota }}</span>
                  </div>
                </div>
              </template>
              <template v-slot:updated_at="item">
                <span>{{ item.updated_at_text }}</span>
              </template>
            </hosting-table>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      transition="custom-dialog-transition"
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()"/>
      <account-configuration-details
        style="width: 560px"
        @modal-close="modalClose"
        :details="modalOptions.message"
        v-if="modalOptions.type == 'email-details'"
      />
      <basic-modal
        v-else
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
      />
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import Api from "../../apis/Api";
import BasicModal from "@/components/modal/BasicModal";
import DataIteratorFilterControls from "../../components/dataIterator/DataIteratorFilterControls.vue";
import HostingTable from "../../components/dataIterator/tables/HostingTable.vue";
import ActionModalMixin from "@/mixins/ActionModalMixin";
import DataIteratorPageMixin from "../../mixins/DataIteratorPageMixin";
import CustomTablePageMixin from "../../mixins/CustomTablePageMixin";
import TableFullScreenMessage from "../../components/dataIterator/tables/TableFullScreenMessage.vue";
import HostingEmailAddressesIllustration from "../../components/illustrations/hosting/hosting-email-addresses-illustration.vue";
import AccountConfigurationDetails from "../../components/modal/AccountConfigurationDetails.vue";
import EmailAddressActionsMixin from "../../mixins/EmailAddressActionsMixin";

export default {
  components: {
    BasicModal,
    DataIteratorFilterControls,
    HostingTable,
    TableFullScreenMessage,
    HostingEmailAddressesIllustration,
    AccountConfigurationDetails,
  },
  mixins: [ActionModalMixin, DataIteratorPageMixin, CustomTablePageMixin, EmailAddressActionsMixin],
  data() {
    return {
      isServerProvided: false,
      addButtonLoading: false,
      itemsPerPage: 8,
      page: 1,
      search: "",
      sortDesc: true,
      sortBy: "updated_at",
      headers: [
        {
          text: 'email',
          value: "email",
          sortable: true,
        },
        {
          text: 'usageQuota',
          value: "usage_quota",
          sortable: true,
        },
        {
          text: 'updated',
          value: "updated_at",
          sortable: true,
        },
        {
          text: 'actions',
          value: "actions",
        },
      ],
      modalRender: 0,
      modalOptions: {open: false},
      highlightItem: {},
      selectedEmailDomain: null,
      emailDomains: {
        loading: false,
        items:
          []
      },
      emailAccounts: {
        loading: false,
        items: []
      },
      isError: false,
    };
  },
  computed: {
    itemsTotal: function () {
      return this.emailAccounts.items.length;
    },
  },
  props: {
    service: Object
  },
  watch: {
    highlightItem: function (newValue) {
      if (!newValue) return;
      setTimeout(() => {
        this.highlightItem = {};
      }, 1000);
    },
    service: function () {
      this.reloadEmailDomains();
    },
    "modalOptions.open": function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
  methods: {
    reloadEmailDomains() {
      this.emailDomains.loading = true;
      this.isError = false;
      Api.cached()
        .get(`/services/${this.service.id}/email/domains`)
        .then((response) => {
          this.emailDomains.items = response.data.data;
          if (response.data.data.length) {
            this.selectedEmailDomain = response.data.data[0];
            this.reloadEmailAccounts();
          } else {
            this.emailAccounts.items = [];
            this.emailAccounts.loading = false;
          }
        })
        .catch((error) => {
          this.isError = true;
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.emailDomains.loading = false;
        });
    },
    reloadEmailAccounts(emailDomain = null) {
      if (emailDomain !== null) {
        this.selectedEmailDomain = emailDomain;
      }
      this.emailAccounts.loading = true;
      this.isError = false;
      if (this.selectedEmailDomain === null) {
        this.emailAccounts.loading = false;
        return;
      }

      Api.get(`/services/${this.service.id}/email/domains/${this.selectedEmailDomain.domain}/accounts`)
        .then((response) => {
          this.emailAccounts.items = response.data.data.map((item) => {
            item.usage_quota = `${this.$t('form.value.sizeMB', {value: item.disk_usage})} / ${
              item.disk_quota == "unlimited"
                ? this.$t('form.value.unlimited')
                : this.$t('form.value.sizeMB', {value: item.disk_quota})
            }`;
            item.detailsButtonLoading = false;
            item.webmailButtonLoading = false;
            item.updated_at_text = item.updated_at
              ? moment(item.updated_at).fromNow()
              : "-";
            return item;
          });
        })
        .catch((error) => {
          this.isError = true;
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.emailAccounts.loading = false;
        });
    },
    itemClass(item) {
      if (this.highlightItem.email && item.email == this.highlightItem.email) {
        return "highlight";
      }
    },
  },
  mounted() {
    if (this.service) {
        this.reloadEmailDomains();
    }
  }
}
</script>

<style scoped lang="scss">
.title-row {
  @media (min-width: 1401px) {
    height: 70px;
  }
}

.v-input.v-select::v-deep {
  fieldset {
    border: none;
  }

  .v-select__selection {
    padding: 0;
    font-size: "$font-size-h3";
    line-height: "$line-height-h3";
    font-weight: "$font-weight-bold";
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden !important;
    @media (min-width: 1401px) {
      max-width: 99%;
    }
  }

  &.v-select .v-select__slot {
    width: unset;
  }
}
</style>
